import {CoverImage, Media, OfferingType, UploadResult} from "../../domain";
import {
  GetCoverImagesApiResponse,
  GetCoverImagesApiResponseItem,
  V2CoverImageUploadResponse,
  V2OfferingType,
  V2PortfolioUploadResponse,
} from "./media.mapper.types";
import {CoverImagesItemResponse} from "../../../../react-query-hooks/products/types";

/**
 * Map domain offering type to v2 API offering type
 * v2 API uses 'live-event' instead of 'live-class'
 */
const DOMAIN_TO_V2_OFFERING_TYPE: Record<OfferingType, V2OfferingType | "portfolio"> = {
  service: "service",
  product: "product",
  "product-service": "product-service",
  "live-class": "live-event",
  portfolio: "portfolio",
};

/**
 * Map v2 API offering type to domain offering type
 * v2 API uses 'live-event', domain uses 'live-class'
 */
const V2_TO_DOMAIN_OFFERING_TYPE: Record<V2OfferingType, OfferingType> = {
  service: "service",
  product: "product",
  "product-service": "product-service",
  "live-event": "live-class",
};

/**
 * MediaMapperV2
 *
 * Maps between v2 API response shapes and domain entities.
 * v2 uploads return only URLs, not full media objects.
 */
export class MediaMapperV2 {
  /**
   * Convert domain offering type to v2 API offering type
   */
  static toV2OfferingType(domainType: OfferingType): V2OfferingType {
    const mapped = DOMAIN_TO_V2_OFFERING_TYPE[domainType];
    if (mapped === "portfolio") {
      throw new Error("Portfolio offering type not supported for cover images");
    }
    return mapped;
  }

  /**
   * Map a URL string to a minimal Media entity
   */
  static urlToDomain(
    url: string,
    index: number,
    offeringType: OfferingType = "portfolio"
  ): Media {
    return Media.create({
      id: `${offeringType}-${index}`,
      url,
      type: "image",
      offeringType,
    });
  }

  /**
   * Map array of URLs to Media entities
   */
  static urlsToDomainList(
    urls: string[],
    offeringType: OfferingType = "portfolio"
  ): Media[] {
    if (!urls || !Array.isArray(urls)) {
      return [];
    }
    return urls.map((url, index) => this.urlToDomain(url, index, offeringType));
  }

  /**
   * Extract upload result from v2 axios response
   * v2 returns { data: { mediaUrls: string[] } }
   */
  static toUploadResult(
    response: V2PortfolioUploadResponse | V2CoverImageUploadResponse,
    offeringType: OfferingType = "portfolio"
  ): UploadResult {
    const mediaUrls = response.data?.mediaUrls ?? [];
    return {
      media: mediaUrls.map((url, index) => ({
        id: `${offeringType}-${index}`,
        url,
        type: "image" as const,
        offeringType,
      })),
    };
  }

  /**
   * Create FormData for cover image upload (v2)
   */
  static toCoverImageFormData(params: {
    file: File;
    offeringId: string | number;
    offeringType: OfferingType;
  }): FormData {
    const formData = new FormData();
    formData.append("cover-image", params.file);
    formData.append("offeringId", String(params.offeringId));
    formData.append("offeringType", this.toV2OfferingType(params.offeringType));
    return formData;
  }

  /**
   * Create FormData for multiple cover image upload (v2)
   */
  static toCoverImageFormDataMultiple(params: {
    files: File[];
    offeringId: string | number;
    offeringType: OfferingType;
  }): FormData {
    const formData = new FormData();
    params.files.forEach((file) => {
      formData.append("cover-image", file);
    });
    formData.append("offeringId", String(params.offeringId));
    formData.append("offeringType", this.toV2OfferingType(params.offeringType));
    return formData;
  }

  /**
   * Create FormData for portfolio image upload (v2)
   */
  static toFormData(params: {file: File; productServiceId: string | number}): FormData {
    const formData = new FormData();
    formData.append("portfolio-image", params.file);
    formData.append("productServiceId", String(params.productServiceId));
    return formData;
  }

  /**
   * Create FormData for multiple portfolio image upload (v2)
   */
  static toFormDataMultiple(params: {
    files: File[];
    productServiceId: string | number;
  }): FormData {
    const formData = new FormData();
    params.files.forEach((file) => {
      formData.append("portfolio-image", file);
    });
    formData.append("productServiceId", String(params.productServiceId));
    return formData;
  }
}

/**
 * Map embedded offering_type to domain OfferingType
 * Embedded API uses "live event" with space, domain uses "live-class"
 */
const EMBEDDED_TO_DOMAIN_OFFERING_TYPE: Record<
  CoverImagesItemResponse["offering_type"],
  OfferingType
> = {
  service: "service",
  product: "product",
  "product-service": "product-service",
  "live event": "live-class",
};

/**
 * Map domain OfferingType to embedded offering_type
 * Domain uses "live-class", embedded API uses "live event" with space
 */
const DOMAIN_TO_EMBEDDED_OFFERING_TYPE: Record<
  OfferingType,
  CoverImagesItemResponse["offering_type"]
> = {
  service: "service",
  product: "product",
  "product-service": "product-service",
  "live-class": "live event",
  portfolio: "product-service", // portfolio is associated with store services
};

/**
 * Maps embedded cover_images data (from offering responses) to domain CoverImage type.
 * Used for fallback when v2 fetch is disabled or fails.
 */
export function mapEmbeddedToDomain(
  items: CoverImagesItemResponse[] | undefined | null
): CoverImage[] {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  return items.map((item) => ({
    id: item.id,
    url: item.media_url,
    type: item.media_type,
    offeringId: item.offering_id,
    offeringType: EMBEDDED_TO_DOMAIN_OFFERING_TYPE[item.offering_type],
    userId: item.user_id,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  }));
}

/**
 * Maps domain CoverImage array to embedded format (CoverImagesItemResponse[]).
 * Used when passing domain data to components that expect API format.
 *
 * This is a temporary bridge during migration. Once all components accept
 * domain types directly, this function can be removed.
 */
export function mapDomainToEmbedded(items: CoverImage[]): CoverImagesItemResponse[] {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  return items.map((item) => ({
    id: item.id,
    media_url: item.url,
    media_type: "image" as const, // Cover images are always images
    offering_id: item.offeringId,
    offering_type: DOMAIN_TO_EMBEDDED_OFFERING_TYPE[item.offeringType],
    user_id: item.userId,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  }));
}

/**
 * CoverImageMapper
 *
 * Maps GET cover images API responses to domain CoverImage type.
 */
export class CoverImageMapper {
  /**
   * Map API response item to domain CoverImage
   */
  static toDomain(item: GetCoverImagesApiResponseItem): CoverImage {
    return {
      id: item.id,
      url: item.mediaUrl,
      type: item.mediaType,
      offeringId: item.entityId,
      offeringType: V2_TO_DOMAIN_OFFERING_TYPE[item.entityType],
      userId: item.userId,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  }

  /**
   * Map API response array to domain CoverImage array
   */
  static toDomainList(response: GetCoverImagesApiResponse): CoverImage[] {
    if (!response || !Array.isArray(response)) {
      return [];
    }
    return response.map((item) => this.toDomain(item));
  }
}
